<template>
  <section
    class="AchievementsPage"
    data-theme="light"
    :aria-busy="isFetchingAchievements"
  >
    <template v-if="!isFetchingAchievements">
      <ul
        v-if="playerAchievements.length > 0"
        class="AchievementsPage__list"
        data-testid="achievementList"
      >
        <li
          v-for="playerAchievement in playerAchievements"
          :key="playerAchievement.id"
          class="AchievementsPage__item"
          :data-level="playerAchievement.achievement.level"
        >
          <Basketball class="AchievementsPage__itemIcon" />
          <h4 class="AchievementsPage__itemTitle">
            {{ playerAchievement.achievement.name }}
          </h4>
          <p class="AchievementsPage__itemDescription">
            {{ playerAchievement.achievement.description }}
          </p>
        </li>
      </ul>
      <section
        v-else
        class="AchievementsPage__emptyState"
        role="status"
      >
        <Icon
          class="AchievementsPage__emptyStateIcon"
          name="trophyWithStar"
        />
        <div class="AchievementsPage__emptyStateContent">
          <p>
            <strong>You don’t have any achievements yet</strong>
          </p>
          <p>
            <em>
              Neither does Shawn Bradley and he’s 7'6!
            </em>
          </p>
        </div>
      </section>
    </template>
  </section>
</template>

<script lang="ts" setup>
import Basketball from "@/components/Basketball.vue";
import Icon from "@/components/Icon.vue";
import { supabaseClient } from "@/lib/database/supabase";
import useAuthStore from "@/stores/useAuthStore";
import { PlayerAchievementWithAchievement } from "@shared/lib/database/query.types";
import { storeToRefs } from "pinia";
import { onMounted, ref } from "vue";

const {
  currentPlayer,
} = storeToRefs(useAuthStore());

const playerAchievements = ref<Array<PlayerAchievementWithAchievement>>([]);
const isFetchingAchievements = ref(true);

onMounted(() => {
  if (!currentPlayer.value) return;

  supabaseClient
    .from("player_achievements")
    .select("*, achievement:achievements!inner(*)")
    .eq("player_id", currentPlayer.value.id)
    .then(({
      data: _playerAchievements,
      error,
    }) => {
      isFetchingAchievements.value = false;

      if (error) {
        alert(error.message);
      } else {
        playerAchievements.value = _playerAchievements || [];
      }
    });
});
</script>

<style lang="scss" scoped>
.AchievementsPage {
  display: grid;
  align-items: start;
  overflow-y: auto;

  background-color: var(--themeColorSurface);
}

.AchievementsPage__emptyState {
  --iconSize: 48px;
  display: grid;
  grid: 1fr min-content 1fr / auto;
  justify-items: center;
  align-self: center;

  font-stretch: var(--fontWidthExtended);
  text-align: center;
  line-height: 1.5;

  strong {
    font-size: 18px;
    font-weight: var(--fontWeightMedium);
  }

  em {
    font-size: 14px;
  }
}

.AchievementsPage__emptyStateIcon {
  margin-bottom: 16px;
  color: var(--themeColorNeutral150);
}

.AchievementsPage__emptyStateContent {
  display: grid;
  gap: 6px;
}

.AchievementsPage__list {
  display: grid;
  gap: 1px;
  outline: solid 1px var(--themeColorNeutral100);
}

.AchievementsPage__item {
  display: grid;
  grid: min-content auto / min-content auto;
  gap: 0 12px;
  padding: 18px 16px 14px;

  outline: dashed 1px var(--themeColorNeutral100);
}

.AchievementsPage__itemIcon {
  grid-row: span 2;
  width: 36px;
  height: 36px;

  [data-level="1"] & {
    --iconColor: #cd7f32; // bronze
  }

  [data-level="2"] & {
    --iconColor: #c0c0c0; // silver
  }

  [data-level="3"] & {
    --iconColor: #d4af37; // gold
  }
}

.AchievementsPage__itemTitle {
  font-size: 18px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
}

.AchievementsPage__itemDescription {
  align-self: start;
  font-stretch: var(--fontWidthExtended);
  line-height: 1.5;
}
</style>
